/* Buses · every bus, its driver, fuel and papers. Design: ConsoleBuses.dc.html.
   Table + side sheet (row click or ?bus=<id>). Export builds a CSV of the
   rows on screen in the browser; nothing is written anywhere. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFleet } from '../data/FleetData';
import type { Bus, PaperDue } from '../data/model';
import { usePage, useShell } from '../Shell';
import { BackOnRoadModal, EditBusModal, OffRoadModal, RemoveBusModal } from '../BusModals';
import { TripModal } from '../TripModal';
import { rosterOf } from '../data/busActions';
import { parkedOf } from '../data/busStatus';
import { openIncidents } from '../data/analytics';
import {
  Chip, ChipTone, Empty, NoResults, PageBody, PageSkeleton, StaleBanner, StatusChip, ZanaNote,
  dayMonth, dueIn, fullDate, n0, plateBeside, plural, useAction, useGo,
} from '../kit';
import { platePlain } from '../data/zana';

const DAY = 86400000;
type Seg = 'all' | 'route' | 'look' | 'idle';
type Col = 'driver' | 'status' | 'aboard' | 'km' | 'kmpl' | 'service' | 'look';
const COLS: { key: Col; label: string }[] = [
  { key: 'driver', label: 'Driver' }, { key: 'status', label: 'Status' }, { key: 'aboard', label: 'Aboard' },
  { key: 'km', label: 'Km, 30 d' }, { key: 'kmpl', label: 'Km per litre' }, { key: 'service', label: 'Next service' },
  { key: 'look', label: 'Needs a look' },
];

const thirsty = (b: Bus) => b.kmpl != null && b.kmplNormal != null && b.kmpl < b.kmplNormal * 0.9;
const service = (b: Bus) => b.papers.find((p) => p.kind === 'service');

/** "9 Nov · 3,100 km", "2 Dec", "In 600 km", "400 km overdue". */
function serviceText(p: PaperDue | undefined): string {
  if (!p) return '';
  const km = p.dueKm != null ? (p.dueKm <= 0 ? `${n0(-p.dueKm)} km overdue` : `${n0(p.dueKm)} km`) : '';
  if (p.due) return km ? `${dayMonth(p.due)} · ${km}` : dayMonth(p.due);
  if (p.dueKm != null) return p.dueKm <= 0 ? km : `In ${km}`;
  return '';
}

function csvCell(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const Buses: React.FC = () => {
  const f = useFleet();
  const { ask, openAddBus } = useShell();
  const [editing, setEditing] = useState<Bus | null>(null);
  const [removing, setRemoving] = useState<Bus | null>(null);
  const [offRoad, setOffRoad] = useState<Bus | null>(null);
  const [onRoad, setOnRoad] = useState<Bus | null>(null);
  const [allRiders, setAllRiders] = useState(false);
  const go = useGo();
  const action = useAction();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [seg, setSeg] = useState<Seg>('all');
  const [cols, setCols] = useState<Set<Col>>(() => new Set(COLS.map((c) => c.key)));
  const [colMenu, setColMenu] = useState(false);
  const colRef = useRef<HTMLDivElement>(null);

  const selId = params.get('bus');
  const sel = selId ? f.buses.find((b) => b.id === selId) || null : null;
  /* One trip open over the sheet (?trip=, which Analytics → Pinned trips links
     to). A pin made there shows here at once; the fleet's runs refresh later. */
  const tripId = params.get('trip');
  const [pins, setPins] = useState<Map<string, string | null>>(new Map());
  const setTrip = useCallback((id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('trip', id); else next.delete('trip');
    setParams(next, { replace: true });
  }, [params, setParams]);
  useEffect(() => { setAllRiders(false); }, [selId]);

  const onRoute = f.buses.filter((b) => b.status === 'on_route' || b.status === 'late');
  const looks = f.buses.filter((b) => b.needsLook);
  const idle = f.buses.filter((b) => b.status === 'idle');
  const worst = f.buses.filter(thirsty).sort((a, b) => (a.kmpl as number) / (a.kmplNormal as number) - (b.kmpl as number) / (b.kmplNormal as number))[0];
  const quiet = f.buses.filter((b) => b.needsLook === 'No refill in 7 days');

  usePage({
    key: 'ConsoleBuses', title: 'Buses',
    zanaContext: sel ? `Buses · ${sel.plate} selected` : 'Buses',
    zanaIntro: 'Ask about any bus in this list.',
    // The selected bus IS the subject of "tell me about this one" — it was only
    // ever in the crumb text, which Zana never received.
    zana: sel ? { entityId: platePlain(sel.plate), focus: [platePlain(sel.plate)] } : { focus: [] },
    prompts: [
      sel ? `Tell me about ${sel.plate} this month` : worst ? `Why is ${worst.plate} burning more fuel?` : 'Which bus drove the most this month?',
      quiet.length ? 'Which buses are not logging refuels?' : 'Which buses have papers expiring soon?',
      'What will fuel cost this term?',
    ],
  });

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return f.buses.filter((b) => {
      if (seg === 'route' && !(b.status === 'on_route' || b.status === 'late')) return false;
      if (seg === 'look' && !b.needsLook) return false;
      if (seg === 'idle' && b.status !== 'idle') return false;
      if (!needle) return true;
      return [b.name, b.plate, b.driverName].some((s) => s.toLowerCase().includes(needle));
    });
  }, [f.buses, q, seg]);

  /* side sheet: Esc closes, focus goes in and comes back */
  const returnTo = useRef<HTMLElement | null>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const [sheetTop, setSheetTop] = useState(64);
  const open = useCallback((id: string) => {
    returnTo.current = document.activeElement as HTMLElement | null;
    const next = new URLSearchParams(params);
    next.set('bus', id);
    setParams(next, { replace: !!selId });
  }, [params, setParams, selId]);
  const close = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete('bus');
    next.delete('trip');
    setParams(next, { replace: true });
    const el = returnTo.current;
    returnTo.current = null;
    setTimeout(() => el?.focus?.(), 0);
  }, [params, setParams]);
  const openId = sel?.id || null;
  useEffect(() => {
    if (!openId) return;
    const h = (document.querySelector('.tc .main > .top') as HTMLElement | null)?.offsetHeight;
    if (h) setSheetTop(h);
    closeBtn.current?.focus();
  }, [openId]);
  useEffect(() => {
    if (!openId) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); close(); } };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [openId, close]);

  /* columns menu */
  useEffect(() => {
    if (!colMenu) return;
    const out = (e: MouseEvent) => { if (!colRef.current?.contains(e.target as Node)) setColMenu(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setColMenu(false); };
    document.addEventListener('mousedown', out); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc); };
  }, [colMenu]);
  const toggleCol = (c: Col) => setCols((s) => { const n = new Set(s); if (n.has(c)) n.delete(c); else n.add(c); return n; });

  const exportCsv = () => {
    const head = ['Bus', 'Plate', 'Driver', 'Status', 'Aboard', 'Children on bus', 'Km, 30 days', 'Km per litre', 'Normal km per litre', 'Next service', 'Needs a look'];
    const lines = rows.map((b) => [
      b.name, b.plate, b.driverName, b.statusLabel, b.aboard, b.expected, b.km30 != null ? Math.round(b.km30) : '',
      b.kmpl != null ? b.kmpl.toFixed(1) : '', b.kmplNormal != null ? b.kmplNormal.toFixed(1) : '', serviceText(service(b)), b.needsLook || '',
    ].map(csvCell).join(','));
    const text = [head.join(','), ...lines].join('\n');
    void action({
      busy: 'Preparing buses.csv…',
      done: 'buses.csv is ready',
      fail: 'Couldn’t prepare buses.csv. Try again.',
      actLabel: 'Download',
      onAct: () => download('buses.csv', text),
      run: async () => rows.length,
    });
  };

  const now = Date.now();
  const lastRuns = sel ? f.runs.filter((r) => r.busId === sel.id && r.type !== 'refuel').slice(0, 6) : [];
  const svcRow = sel ? f.maintenance?.find((m) => m.busId === sel.id) : undefined;

  const paperLine = (label: string, p: PaperDue | undefined, last: boolean) => {
    let chip: React.ReactNode;
    let after = '';
    if (!p) chip = <span style={{ color: 'var(--faint)' }}>Not on file</span>;
    else if (p.kind === 'service') {
      const overdue = (p.dueKm != null && p.dueKm <= 0) || svcRow?.service.state === 'overdue';
      const soon = svcRow?.service.state === 'due_soon' || (p.due != null && p.due.getTime() - now < 14 * DAY);
      const tone: ChipTone = overdue ? 'bad' : soon ? 'warn' : 'ok';
      const text = p.dueKm != null ? (p.dueKm <= 0 ? `${n0(-p.dueKm)} km overdue` : `In ${n0(p.dueKm)} km`) : p.due ? dueIn(p.due).text : 'On file';
      chip = <Chip tone={tone}>{text}</Chip>;
      after = p.due ? ` about ${dayMonth(p.due)}` : '';
    } else if (p.due) {
      const d = dueIn(p.due).days;
      chip = d < 0 ? <Chip tone="bad">Expired</Chip> : d <= 14 ? <Chip tone="warn">{dueIn(p.due).text}</Chip> : <Chip tone="ok">Valid</Chip>;
      after = ` ${d < 0 ? 'since' : 'to'} ${fullDate(p.due)}`;
    } else chip = <span style={{ color: 'var(--faint)' }}>No date on file</span>;
    return (
      <div className="row" style={{ justifyContent: 'space-between', padding: '10px 0', borderBottom: last ? undefined : '1px solid var(--line)' }}>
        <span>{label}</span>
        <span className="sm">{chip}{after}</span>
      </div>
    );
  };

  const segBtn = (k: Seg, label: string, n: number) => (
    <button type="button" className={seg === k ? 'on' : ''} aria-pressed={seg === k} onClick={() => setSeg(k)}>{label} {n}</button>
  );
  const show = (c: Col) => cols.has(c);

  return (
    <PageBody loading={f.loading} skeleton={<PageSkeleton stats rows={7} />}>
      <div className={'wrap' + (f.error ? ' stale' : '')} style={{ maxWidth: '1240px' }}>
        {f.error && <StaleBanner what="buses" since={f.updatedAt} onRetry={f.retry} />}
        <div className="phead">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <h1 className="h1">Buses</h1>
            <p className="sub">
              {f.buses.length === 0 ? 'No buses yet'
                : `${plural(f.buses.length, 'bus', 'buses')} · ${onRoute.length} on route · ${looks.length} need${looks.length === 1 ? 's' : ''} a look`}
            </p>
          </div>
          <div className="row" style={{ gap: '8px' }}>
            {f.buses.length > 0 && (
              <button type="button" className="btn b-line" onClick={exportCsv}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                  <path d="M12 3v12M7 10l5 5 5-5" />
                  <path d="M5 21h14" />
                </svg>
                Export
              </button>
            )}
            <button type="button" className="btn b-ink" onClick={openAddBus}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add bus
            </button>
          </div>
        </div>

        {f.buses.length === 0 ? (
          <section className="panel">
            <Empty icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="3" width="16" height="15" rx="3" /><path d="M4 11h16M8 21v-3M16 21v-3" /></svg>}
              title="Add your first bus" body="Add a bus and its driver. The driver signs in with a code, taps Start, and the bus appears here and on your map.">
              <button type="button" className="btn b-ink" onClick={openAddBus}>Add a bus</button>
            </Empty>
          </section>
        ) : (
          <section className="panel" style={{ overflow: 'hidden' }}>
            <div className="row" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', flexWrap: 'wrap', gap: '10px' }}>
              <div className="zinput" style={{ height: '40px', width: '280px', maxWidth: '100%', borderRadius: '8px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
                <input type="text" placeholder="Search bus, plate or driver" aria-label="Search buses" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <div className="seg" role="group" aria-label="Show">
                {segBtn('all', 'All', f.buses.length)}
                {segBtn('route', 'On route', onRoute.length)}
                {segBtn('look', 'Needs a look', looks.length)}
                {segBtn('idle', 'Idle', idle.length)}
              </div>
              <span style={{ flex: '1' }} />
              <div ref={colRef} style={{ position: 'relative' }}>
                <button type="button" className="btn s b-line" aria-expanded={colMenu} aria-haspopup="true" onClick={() => setColMenu((o) => !o)}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <path d="M9 4v16M15 4v16" />
                  </svg>
                  Columns
                </button>
                {colMenu && (
                  <div className="panel" style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 8, padding: '8px 4px', minWidth: 200, boxShadow: 'var(--float)' }}>
                    {COLS.map((c) => (
                      <label key={c.key} className="row" style={{ gap: 10, padding: '6px 12px', cursor: 'pointer', fontSize: 14 }}>
                        <input type="checkbox" checked={cols.has(c.key)} onChange={() => toggleCol(c.key)} />
                        {c.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {rows.length === 0 ? (
              q.trim() ? (
                <NoResults what="bus" q={q.trim()} hint={`Search looks at bus names, number plates and drivers${seg !== 'all' ? ' in this filter. Try All, or' : '. Try'} part of a plate, like “${f.buses[0]?.plate.split(' ')[0] || 'KDJ'}”.`} onClear={() => setQ('')} />
              ) : (
                <Empty title={seg === 'route' ? 'No bus is on the road right now' : seg === 'look' ? 'No bus needs a look' : 'No bus is idle'}
                  body={seg === 'route' ? 'Buses appear here when a driver taps Start in the Trasse app.' : 'Everything is in order for this filter.'}>
                  <button type="button" className="btn s b-line" onClick={() => setSeg('all')}>Show all buses</button>
                </Empty>
              )
            ) : (
              <div className="tblbox">
                <table className="t">
                  <thead>
                    <tr>
                      <th>Bus</th>
                      {show('driver') && <th>Driver</th>}
                      {show('status') && <th>Status</th>}
                      {show('aboard') && <th>Aboard</th>}
                      {show('km') && <th className="r">Km, 30 d</th>}
                      {show('kmpl') && <th className="r">Km per litre</th>}
                      {show('service') && <th>Next service</th>}
                      {show('look') && <th>Needs a look</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((b) => {
                      const svc = serviceText(service(b));
                      return (
                        <tr key={b.id} className={sel?.id === b.id ? 'sel' : ''} onClick={() => open(b.id)} style={{ cursor: 'pointer' }} data-z={`bus:${b.id}`}>
                          <td>
                            <div className="who">
                              <b>
                                <button type="button" onClick={(e) => { e.stopPropagation(); open(b.id); }}
                                  style={{ border: 0, background: 'transparent', padding: 0, font: 'inherit', color: 'inherit', textAlign: 'left', cursor: 'pointer' }}>
                                  {b.name}
                                </button>
                              </b>
                              {plateBeside(b) && <span>{b.plate}</span>}
                            </div>
                          </td>
                          {show('driver') && <td>{b.driverName || <span style={{ color: 'var(--faint)' }}>—</span>}</td>}
                          {show('status') && <td><StatusChip status={b.status} label={b.statusLabel} /></td>}
                          {show('aboard') && <td className="num">{b.aboard != null ? `${b.aboard} of ${b.expected ?? '—'}` : <span style={{ color: 'var(--faint)' }}>—</span>}</td>}
                          {show('km') && <td className="r num">{b.km30 != null ? n0(b.km30) : <span style={{ color: 'var(--faint)' }}>—</span>}</td>}
                          {show('kmpl') && (
                            <td className="r num" style={thirsty(b) ? { color: 'var(--bad)', fontWeight: '600' } : undefined}>
                              {b.kmpl != null ? b.kmpl.toFixed(1) : <span style={{ color: 'var(--faint)' }}>—</span>}
                            </td>
                          )}
                          {show('service') && <td className="num">{svc || <span style={{ color: 'var(--faint)' }}>—</span>}</td>}
                          {show('look') && (
                            <td>
                              {b.needsLook
                                ? <span className={`chip c-${/expired|overdue/i.test(b.needsLook) ? 'bad' : 'warn'}`} style={{ height: '22px', fontSize: '12px' }}>{b.needsLook}</span>
                                : <span style={{ color: 'var(--faint)' }}>—</span>}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>

      {sel && (
        <aside className="sheet" aria-label={sel.plate} style={{ top: sheetTop }}>
          <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--line)', display: 'flex', gap: '12px', alignItems: 'flex-start' }} data-z={`sheet:${sel.id}`}>
            <div style={{ flex: '1' }}>
              <span className="mono">{[sel.plate, sel.model, sel.seats != null ? `${sel.seats} seats` : ''].filter(Boolean).join(' · ')}</span>
              <h2 className="h2" style={{ fontSize: '24px', marginTop: '4px' }}>{sel.name}</h2>
              <div className="row" style={{ gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                <StatusChip status={sel.status} label={sel.statusLabel} />
                {sel.needsLook && <Chip tone={/expired|overdue/i.test(sel.needsLook) ? 'bad' : 'warn'}>{sel.needsLook}</Chip>}
              </div>
            </div>
            <button ref={closeBtn} type="button" className="ibtn" aria-label="Close" onClick={close}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px', overflow: 'auto', flex: '1' }}>
            {thirsty(sel) && (
              <ZanaNote label="Ask why" onAsk={() => ask(`Why is ${sel.plate} burning more fuel?`)}>
                Using {Math.round(((sel.kmplNormal as number) / (sel.kmpl as number) - 1) * 100)}% more fuel than its normal over the last 30 days.
              </ZanaNote>
            )}
            <div className="kv">
              <div><span>Driver</span><b>{sel.driverName || <span style={{ color: 'var(--faint)' }}>No driver</span>}</b></div>
              <div><span>Today</span><b className="num">{sel.aboard != null ? `${sel.aboard} of ${sel.expected ?? '—'} aboard` : sel.expected ? `${sel.expected} children` : '—'}</b></div>
              <div><span>Km, 30 days</span><b className="num">{sel.km30 != null ? n0(sel.km30) : '—'}</b></div>
              <div>
                <span>Km per litre</span>
                <b className="num" style={thirsty(sel) ? { color: 'var(--bad)' } : undefined}>
                  {sel.kmpl != null ? sel.kmpl.toFixed(1) : '—'}
                  {sel.kmplNormal != null && <span style={{ fontSize: '13px', color: 'var(--muted)' }}> normal {sel.kmplNormal.toFixed(1)}</span>}
                </b>
              </div>
            </div>
            {(() => {
              /* Who rides it — names only; the rest behind "+N more" (2026-10-28). */
              const riders = rosterOf(sel).map((c) => c.name).filter(Boolean);
              const parked = parkedOf(sel).kids.length;
              const open = openIncidents(f).filter((r) => r.busId === sel.id).length;
              const shownRiders = allRiders ? riders : riders.slice(0, 8);
              return (
                <>
                  <div>
                    <h3 className="h3" style={{ marginBottom: '8px' }}>Children on it</h3>
                    {sel.status === 'off_road' ? (
                      <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>
                        {parked ? `Off the road — its ${plural(parked, 'child rides', 'children ride')} other buses until it is back.` : 'Off the road. No children were on it.'}
                      </p>
                    ) : riders.length === 0 ? (
                      <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>No children on its list yet.</p>
                    ) : (
                      <p style={{ margin: 0, lineHeight: '22px' }}>
                        {shownRiders.join(', ')}
                        {riders.length > shownRiders.length && (
                          <> <button type="button" onClick={() => setAllRiders(true)} style={{ all: 'unset', cursor: 'pointer', color: 'var(--teal-ink)', fontWeight: 600 }}>…+{riders.length - shownRiders.length} more</button></>
                        )}
                        {allRiders && riders.length > 8 && <> <button type="button" onClick={() => setAllRiders(false)} style={{ all: 'unset', cursor: 'pointer', color: 'var(--muted)', fontWeight: 600 }}>Show less</button></>}
                      </p>
                    )}
                  </div>
                  {open > 0 && (
                    <div className="row" style={{ gap: 10 }}>
                      <span className="chip c-bad">{plural(open, 'open incident')}</span>
                      <a className="btn s b-line" {...go('ConsoleAnalytics', '?tab=incidents')}>See incidents</a>
                    </div>
                  )}
                </>
              );
            })()}
            <div>
              <h3 className="h3" style={{ marginBottom: '8px' }}>Papers and service</h3>
              {paperLine('Insurance', sel.papers.find((p) => p.kind === 'insurance'), false)}
              {paperLine('NTSA inspection', sel.papers.find((p) => p.kind === 'inspection'), false)}
              {paperLine('Next service', service(sel), true)}
            </div>
            <div>
              <h3 className="h3" style={{ marginBottom: '8px' }}>Last runs</h3>
              {lastRuns.length === 0 ? (
                <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>No runs in the last 30 days.</p>
              ) : lastRuns.map((r, i) => {
                const pin = pins.has(r.id) ? pins.get(r.id) : r.pinnedName;
                return (
                  <button key={r.id} type="button" className="row" onClick={() => setTrip(r.id)} title="See this trip on the map"
                    style={{ all: 'unset', boxSizing: 'border-box', display: 'flex', width: '100%', alignItems: 'center', gap: 10, cursor: 'pointer', justifyContent: 'space-between', padding: '9px 0', borderBottom: i < lastRuns.length - 1 ? '1px solid var(--line)' : undefined }}>
                    <span style={{ minWidth: 0 }}>
                      {r.date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · {r.type}
                      {pin && <span className="chip c-mute" style={{ marginLeft: 8, height: 22 }}>{pin}</span>}
                    </span>
                    <span className="num sm" style={{ color: 'var(--muted)', flex: 'none' }}>
                      {[r.km != null && r.km > 0 ? `${n0(r.km)} km` : '', r.total ? plural(r.served, 'child', 'children') : '', r.status === 'cancelled' ? 'cancelled' : ''].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ padding: '14px 20px', borderTop: '1px solid var(--line)', display: 'flex', gap: '8px' }}>
            <a className="btn b-ink" {...go('ConsoleLiveMap', `?bus=${encodeURIComponent(sel.id)}`)} style={{ flex: '1' }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2z" />
                <path d="M9 4v14M15 6v14" />
              </svg>
              See on map
            </a>
            {sel.driverPhone && (
              <a className="btn b-line" href={`tel:${sel.driverPhone.replace(/\s+/g, '')}`} style={{ flex: '1' }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                  <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2Z" />
                </svg>
                Call {sel.driverName.split(/\s+/)[0] || 'driver'}
              </a>
            )}
          </div>
          <div style={{ padding: '0 20px 14px', display: 'flex', gap: '8px' }} data-z={`offroad:${sel.id}`}>
            <button type="button" className="btn s b-line" style={{ flex: '1' }} onClick={() => setEditing(sel)}>Edit details</button>
            {sel.status === 'off_road'
              ? <button type="button" className="btn s b-line" style={{ flex: '1' }} onClick={() => setOnRoad(sel)}>Back on the road</button>
              : <button type="button" className="btn s b-line" style={{ flex: '1' }} onClick={() => setOffRoad(sel)}>Take off the road</button>}
          </div>
          <div style={{ padding: '0 20px 14px', display: 'flex', gap: '8px' }}>
            <button type="button" className="btn s b-ghost" style={{ flex: '1', color: 'var(--bad)' }} onClick={() => setRemoving(sel)}>Remove bus</button>
          </div>
        </aside>
      )}
      {editing && <EditBusModal bus={editing} onClose={() => setEditing(null)} />}
      {sel && tripId && (
        <TripModal f={f} bus={sel} runId={tripId} onClose={() => setTrip(null)}
          onPinned={(name) => setPins((m) => new Map(m).set(tripId, name))} />
      )}
      {removing && <RemoveBusModal f={f} bus={removing} onClose={() => setRemoving(null)} onRemoved={close} />}
      {offRoad && <OffRoadModal f={f} bus={offRoad} onClose={() => setOffRoad(null)} />}
      {onRoad && <BackOnRoadModal f={f} bus={onRoad} onClose={() => setOnRoad(null)} />}
    </PageBody>
  );
};

export default Buses;
